
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLang } from '@/contexts/LangContext';
import { ArrowRight, TrendingUp, ShieldCheck, BarChart3 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import StrategyCard from '@/components/marketplace/StrategyCard';
import { getExchangeLogo, getRiskLabel, formatDate } from '@/components/marketplace/utils/marketplace-utils';
import { useAccountPerformance } from '@/hooks/marketplace/useAccountPerformance';
import StrategyDetail from '@/components/StrategyDetail';
import { MarketplaceStrategy } from '@/types/strategy-types';

const StrategyShowcaseSection = () => {
  const { t } = useLang();
  const navigate = useNavigate();
  const [strategies, setStrategies] = useState<MarketplaceStrategy[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedStrategy, setSelectedStrategy] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  
  // Check authentication status
  useEffect(() => {
    const checkAuth = async () => {
      const { data } = await supabase.auth.getSession();
      setIsAuthenticated(!!data.session);
    };
    
    checkAuth();
    
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setIsAuthenticated(!!session);
    });
    
    return () => subscription.unsubscribe();
  }, []);
  
  // Load the strategies from Supabase
  useEffect(() => {
    const fetchStrategies = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('strategy')
          .select('*')
          .eq('exchange', 'OKX') // Filter for OKX strategies
          .eq('approved', true)
          .eq('active', true)
          .order('expected_monthly_profit', { ascending: false })
          .limit(3);
        
        if (error) {
          console.error('Error fetching OKX strategies:', error);
          return;
        }
        
        // Transform the data to match the MarketplaceStrategy interface
        const transformedStrategies: MarketplaceStrategy[] = data?.map(strategy => ({
          id: strategy.id,
          strategy_name: strategy.name || "Untitled Strategy",
          name: strategy.name || "Untitled Strategy", 
          min_investment: strategy.min_investment || 0,
          risk_level: strategy.risk_level || 1,
          exchange: strategy.exchange || "OKX",
          followers_count: 0,
          performance_percentage: strategy.expected_monthly_profit || 0,
          performance_since: strategy.verified_since ? new Date(strategy.verified_since).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
          expected_monthly_profit: strategy.expected_monthly_profit,
          verified_since: strategy.verified_since,
          is_pro: strategy.plan === 'premium' || strategy.plan === 'pro',
          plan: strategy.plan,
          active: strategy.active,
          description: strategy.description || "Trading strategy",
          win_rate: null,
          max_drawdown: strategy.defined_max_drawdown,
          trades_won: null,
          trades_lost: null,
          total_aum: null,
          total_followers: null
        })) || [];
        
        setStrategies(transformedStrategies);
      } catch (err) {
        console.error("Error in fetchStrategies:", err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchStrategies();
  }, []);
  
  // Get performance data for the strategies
  const { accountPerformance, performanceLoading } = useAccountPerformance(strategies, loading);
  
  // Find the selected strategy object
  const selectedStrategyObject = selectedStrategy 
    ? strategies.find(s => (s.name || s.strategy_name) === selectedStrategy) 
    : null;
  
  // Helper function to find the strategy ID
  const findStrategyId = (strategyName: string | null): string => {
    if (!strategyName) return '';
    const strategy = strategies.find(s => {
      const sName = s.name || s.strategy_name;
      return sName === strategyName;
    });
    return strategy?.id || '';
  };
  
  // Handle selecting a strategy to view details
  const handleSelectStrategy = (strategyName: string) => {
    setSelectedStrategy(strategyName);
  };
  
  // Show fallback content if no strategies are found
  if (!loading && strategies.length === 0) {
    return (
      <div className="py-16 px-4 bg-gray-900">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-2 text-white">
            {t.strategyShowcase.title}
          </h2>
          <p className="text-center text-gray-300 mb-12">
            {t.strategyShowcase.subtitle}
          </p>
          
          <div className="text-center text-gray-400 my-12">
            <p>Top performing strategies will be available soon.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-16 px-4 bg-gray-900">
      <div className="max-w-5xl mx-auto">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-2 text-white">
          {t.strategyShowcase.title}
        </h2>
        <p className="text-center text-gray-300 mb-12">
          {t.strategyShowcase.subtitle}
        </p>
        
        {/* Updated grid layout with conditional classes for centering when there are 2 cards */}
        <div className={`grid grid-cols-1 ${
          strategies.length === 2 
            ? "md:grid-cols-2 md:mx-auto md:max-w-2xl" 
            : "md:grid-cols-3"
        } gap-6 mb-10 place-items-center justify-center`}>
          {loading || performanceLoading ? (
            // Loading skeleton
            Array(3).fill(0).map((_, index) => (
              <div key={index} className="bg-black border border-gray-800 rounded-lg p-6 h-[400px] animate-pulse w-full">
                <div className="w-full h-6 bg-gray-800 rounded mb-4"></div>
                <div className="w-3/4 h-4 bg-gray-800 rounded mb-6"></div>
                <div className="w-full h-40 bg-gray-800 rounded"></div>
              </div>
            ))
          ) : (
            // Real strategies - with wrapper to ensure equal height
            strategies.map((strategy) => (
              <div key={strategy.id} className="h-full w-full max-w-sm flex">
                <div className="flex-1 flex okx-landing-card">
                  <StrategyCard
                    strategy={strategy}
                    onSelect={handleSelectStrategy}
                    getRiskLabel={getRiskLabel}
                    formatDate={formatDate}
                    getExchangeLogo={getExchangeLogo}
                    performanceData={accountPerformance[strategy.id]}
                    followLabel={isAuthenticated ? "Follow Now" : "Sign Up & Follow"}
                  />
                </div>
              </div>
            ))
          )}
        </div>
        
        {/* Only show "View All" button if we have 3 or more strategies */}
        {strategies.length >= 3 && (
          <div className="flex justify-center">
            <Link to="/marketplace?source=okx">
              <Button className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 text-lg rounded-lg flex items-center gap-2">
                {t.strategyShowcase.viewAll} <ArrowRight className="ml-1" size={18} />
              </Button>
            </Link>
          </div>
        )}
      </div>
      
      {/* Strategy Detail Modal with dark theme for OKX landing */}
      {selectedStrategyObject && (
        <div className="okx-strategy-detail">
          <StrategyDetail
            isOpen={!!selectedStrategy}
            onClose={() => setSelectedStrategy(null)}
            strategy={selectedStrategyObject}
            performanceData={selectedStrategy ? accountPerformance[findStrategyId(selectedStrategy)] : undefined}
          />
        </div>
      )}
    </div>
  );
};

export default StrategyShowcaseSection;
